import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { bindActionCreators } from '@reduxjs/toolkit';
import { getCurrentUser } from 'aws-amplify/auth';
import { userHasAccess } from '../../services/RoleBasedAccess';
import { login } from '../../state/account/action_creator';
import { fetchSystems, updateSelectedSystem } from '../../state/system/action_creator';
import { System } from '../../state/types';
import { State } from '../../state/reducers';

interface GroupedSystem {
  label: string;
  systems: { code: string; label: string }[];
}

const GROUP_ORDER: string[] = [
  "Car Dealers",
  "Alliance Auto Autions",
  "America's Auto Actions",
  "Dealers Auto Auctions",
  "Manheim Auto Auctions",
  "Metro Auto Auctions",
  "Boats",
  "RV",
  "Internal"
];

export const useNavbarLogic = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const userData = useSelector((state: State) => state.accountState.user);
  const { selectedSystem, systems } = useSelector((state: State) => state.systemState);

  // You can keep bindActionCreators or just use dispatch directly
  const updateSelectedSystemAction = bindActionCreators(updateSelectedSystem, dispatch)
  const loginAction = bindActionCreators(login, dispatch)
  const fetchSystemsAction = bindActionCreators(fetchSystems, dispatch)

  // Effect for fetching systems and setting initial system
  useEffect(() => {
    fetchSystemsAction();
    const savedSystem = localStorage.getItem('selectedSystem');
    if (savedSystem && !selectedSystem) {
      updateSelectedSystemAction(savedSystem);
    }
  }, []); // Runs once on mount

  // Effect for checking user authentication
  useEffect(() => {
    getCurrentUser()
      .then(currentUser => {
        if (!currentUser) navigate('/logout');
        else if (currentUser.userId !== userData?.username) loginAction(currentUser.userId);
      })
      .catch(() => navigate('/login'));
  }, []); // Runs once on mount

  const clearVehicleNavStateAndNavigate = (path: string) => {
    sessionStorage.removeItem('vehiclesNavState');
    navigate(path);
  };

  const getCommonNameForSystem = (systemCode: string) => {
    return systems?.find((sys: System) => sys.systemCode === systemCode)?.commonName;
  };

  const accessibleSystemCodes = userHasAccess(userData?.role, ['Internal'])
    ? (systems || []).map((sys: System) => sys.systemCode)
    : userData?.system_access || [];

  const groupAndFilterSystems = useCallback((searchTerm: string): GroupedSystem[] => {
    const groupMap: { [key: string]: GroupedSystem } = {};
    const otherGroup: GroupedSystem = { label: "Other", systems: [] };

    GROUP_ORDER.forEach((label) => {
      groupMap[label] = { label, systems: [] };
    });

    accessibleSystemCodes.forEach((code: string) => {
      const system = systems.find((s: System) => s.systemCode === code);
      if (!system) return;

      const name = system.commonName || "Unknown";
      const systemLabel = `${name} (${code})`;

      const isMatch =
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        code.toLowerCase().includes(searchTerm.toLowerCase());

      if (!isMatch) {
        return;
      }

      const category = system.system_category;
      if (category && groupMap[category]) {
        groupMap[category].systems.push({ code, label: systemLabel });
      } else {
        otherGroup.systems.push({ code, label: systemLabel });
      }
    });

    Object.values(groupMap).forEach((group) =>
      group.systems.sort((a, b) => a.label.localeCompare(b.label))
    );
    otherGroup.systems.sort((a, b) => a.label.localeCompare(b.label));

    const allGroups = [
      ...GROUP_ORDER.map(label => groupMap[label]),
      otherGroup,
    ];

    return allGroups.filter((group) => group.systems.length > 0);
  }, [systems, accessibleSystemCodes]);

  return {
    userData,
    isLoggedIn: !!userData?.username,
    isLogoutPage: location.pathname === '/logout',
    systems,
    selectedSystem,
    selectedSystemName: getCommonNameForSystem(selectedSystem || '') || 'Select a System',
    accessibleSystemCodes,
    handleNavigation: clearVehicleNavStateAndNavigate,
    handleSystemSelect: (systemCode: string) => {
      localStorage.setItem('selectedSystem', systemCode);
      updateSelectedSystemAction(systemCode);
      if (location.pathname !== '/systems') navigate('/');
    },
    groupAndFilterSystems
  };
};